import React, { useState, useEffect, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { loggedIn, login } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loggedIn === true) {
      navigate("/");
    }
  }, [loggedIn, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        login();
      } else {
        setError("That doesn't look right — try again!");
      }
    } catch {
      setError("Couldn't reach the kitchen. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "hsl(40 33% 96%)" }}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-border/40 p-8 flex flex-col items-center gap-6"
      >
        <div className="flex flex-col items-center gap-3">
          <img
            src="/logo.png"
            alt="Family Cookbook"
            className="w-16 h-16 object-contain"
          />
          <h1
            className="text-2xl font-bold text-foreground text-center"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Family Cookbook
          </h1>
          <p className="text-sm text-muted-foreground text-center leading-snug">
            Welcome back — come on in.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="w-full flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="username"
              className="text-sm font-medium text-foreground"
            >
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
              placeholder="family"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="password"
              className="text-sm font-medium text-foreground"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
            style={{
              background: loading ? "hsl(17 53% 65%)" : "hsl(17 53% 51%)",
              color: "hsl(40 33% 97%)",
            }}
          >
            {loading ? "Opening the door…" : "Enter the Kitchen"}
          </button>

          {error && (
            <p
              className="text-sm text-center font-medium"
              style={{ color: "hsl(0 65% 50%)" }}
            >
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
